// 1:1 with api (v2 snake_case)
export type Evaluation = {
  id: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  name: string;
  description?: string;
  metric_providers: {
    metric_provider_id: string;
    version_id: string;
  }[];
  is_deprecated: boolean;
};

export type MetricProvider = {
  id: string;
  name: string;
  description?: string;
  versions: MetricProviderVersion[];
  created_at: string;
};

export type MetricProviderVersion = {
  version: number;
  id: string;
};

export type MetricConfigAPIHeader = {
  header: string;
  value: string;
};

export type MetricConfigAPI = {
  url: string;
  headers: MetricConfigAPIHeader[]
  is_async: boolean | null;
};

export type MetricConfigLLMScore = {
  prompt: string;
  model_parameters: {
    model: string;
    temperature?: number | 'default' | null;
  };
  prompt_parameters?: Record<string, string> | null;
};

export type MetricConfig = {
  id: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  name: string;
  metric_provider_id: string;
  version_id: string;
  version?: number | null;
  example_type: ExampleTypeEnum[] | null;
  configuration: MetricConfigAPI | MetricConfigLLMScore;
  produces_metrics: string[];
  is_deprecated: boolean;
  description?: string;
  latest_version_number: number;
  model_name?: string | null;
  is_editable?: boolean;
  is_system?: boolean;
};

export type Run = {
  id: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  evaluation_id: string;
  source_id?: string;
  source_version?: number;
  source_type: SourceTypeEnum;
  schedule_id?: string;
  status: string;
  status_reason?: string;
  total_examples?: number;
  total_results?: number;
  started_at?: string;
  completed_at?: string;
};

export type Result = {
  id: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  run_id: string;
  example_id: string;
  example_type: string;
  metric_provider_id: string;
  metric_provider_version_id: string;
  metric_name: string;
  score: string | null;
  execution_time: number;
  score_meta_data: {
    reason?: string;
  } | null;
  result_status: string;
  result_status_reason?: string;
};

export type SourceConfigSnowflake = {
  credential_ref: string;
  database_name: string;
  query: string;
  schema_name?: string;
};

export type SourceConfigJson = Record<string, unknown>;

export type SourceConfigAgentLogs = {
  filters: {
    agent_name?: string | null;
    agent_id?: string | null;
    component_name?: string | null;
    constellation_name?: string | null;
    constellation_version?: string | null;
    client_id?: string | null;
    correlation_id?: string | null;
    request_id?: string | null;
    metadata?: Record<string, unknown> | null;
    from_start_time?: string | null;
    to_end_time?: string | null;
  };
  row_limit?: number | null;
};

export type SourceConfigModelInvocationLogs = {
  filters: {
    agent_name?: string | null;
    agent_id?: string | null;
    constellation_name?: string | null;
    constellation_version?: string | null;
    client_id?: string | null;
    correlation_id?: string | null;
    request_id?: string | null;
    requester_tags?: string[] | null;
    requester_metadata?: Record<string, unknown> | null;
    from_start_time?: string | null;
    to_end_time?: string | null;
  };
  row_limit?: number | null;
  context_window?: number | null;
};

export type SourceConfigCompletionLogs = {
  filters: {
    agent_name?: string | null;
    agent_id?: string | null;
    constellation_name?: string | null;
    constellation_version?: string | null;
    client_id?: string | null;
    correlation_id?: string | null;
    request_id?: string | null;
    from_start_time?: string | null;
    to_end_time?: string | null;
  };
  row_limit?: number | null;
};

export type SourceConfig = SourceConfigSnowflake | SourceConfigJson | SourceConfigAgentLogs | SourceConfigModelInvocationLogs | SourceConfigCompletionLogs;

type SourceBase = {
  id: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  name: string;
  description?: string;
  status: 'active' | 'error';
  example_type: 'message' | 'conversation';
  example_count?: number;
  last_sync?: string;
  is_deprecated: boolean;
  version: number;
  version_created_at: string;
  version_updated_at?: string | null;
  version_created_by?: string | null;
  latest_version_number: number;
};

export type SourceJson = SourceBase & { source_type: 'json', config: SourceConfigJson };
export type SourceSnowflake = SourceBase & { source_type: 'snowflake', config: SourceConfigSnowflake };
export type SourceAgentLogs = SourceBase & { source_type: 'mas_agent_action_log', config: SourceConfigAgentLogs };
export type SourceModelInvocationLogs = SourceBase & { source_type: 'mas_model_invocation_log', config: SourceConfigModelInvocationLogs };
export type SourceCompletionLogs = SourceBase & { source_type: 'mas_completion_log', config: SourceConfigCompletionLogs };

export type Source = SourceJson | SourceSnowflake | SourceAgentLogs | SourceModelInvocationLogs | SourceCompletionLogs;

export type SourcePreview = {
  source_id: string;
  items: Example[];
  total_count: number;
  preview_count: number;
};
export type Details<T extends object = object> = {
  details?: T
};

export type DetailRunInfo = {
  last_run_date: string | null;
  last_run_status?: string | null;
  run_count: number;
};

export type DetailMetricInfo = {
  metric_provider_id: string;
  metric_name: string;
  num_results: number;
  num_numeric_results: number;
  average_score: number | null;
  completion_status: string;
};

export type DetailMetricsInfo = {
  metrics: DetailMetricInfo[];
};

export type EvaluationWithDetails<T extends object = object> = {
  evaluation: Evaluation;
} & Details<T>;

export type RunWithDetails<T extends object = object> = {
  run: Run;
} & Details<T>;

export const METRIC_TYPES = {
  API_CLASSIFIER: 'API Classifier',
  LLM_AS_A_JUDGE: 'LLM as a Judge'
} as const;
export type MetricTypeEnum = typeof METRIC_TYPES[ keyof typeof METRIC_TYPES ];

export const SOURCE_TYPES = {
  JSON: 'json',
  SNOWFLAKE: 'snowflake',
  AGENT_ACTION_LOGS: 'mas_agent_action_log',
  MODEL_INVOCATION_LOGS: 'mas_model_invocation_log',
  COMPLETION_LOGS: 'mas_completion_log'
} as const;
export type SourceTypeEnum = typeof SOURCE_TYPES[ keyof typeof SOURCE_TYPES ];

export const SOURCE_TYPE_DISPLAY_NAMES: Record<SourceTypeEnum, string> = {
  [ SOURCE_TYPES.JSON ]: 'JSON',
  [ SOURCE_TYPES.SNOWFLAKE ]: 'Snowflake',
  [ SOURCE_TYPES.AGENT_ACTION_LOGS ]: 'Agent Action Logs',
  [ SOURCE_TYPES.MODEL_INVOCATION_LOGS ]: 'Model Invocation Logs',
  [ SOURCE_TYPES.COMPLETION_LOGS ]: 'Request/Response Logs'
};

// Helper subtitles shown beneath each option label in the Source Type dropdown.
export const SOURCE_TYPE_HELPER: Record<SourceTypeEnum, string> = {
  [ SOURCE_TYPES.JSON ]: 'JSON examples',
  [ SOURCE_TYPES.SNOWFLAKE ]: 'Database examples',
  [ SOURCE_TYPES.AGENT_ACTION_LOGS ]: 'SDK-emitted agent events',
  [ SOURCE_TYPES.MODEL_INVOCATION_LOGS ]: 'Model requests',
  [ SOURCE_TYPES.COMPLETION_LOGS ]: 'Request lifecycle'
};

export const EXAMPLE_TYPES = {
  CONVERSATION: 'conversation',
  MESSAGE: 'message'
} as const;
export type ExampleTypeEnum = typeof EXAMPLE_TYPES[ keyof typeof EXAMPLE_TYPES ];

export const EXAMPLE_TYPE_DISPLAY_NAMES: Record<ExampleTypeEnum, string> = {
  [ EXAMPLE_TYPES.CONVERSATION ]: 'Conversation',
  [ EXAMPLE_TYPES.MESSAGE ]: 'Message'
};

/**
 * A single turn in an Example. Mirrors aidp-eval's `Message` discriminated
 * union: a turn carries either textual content (`message`) or a structured
 * payload (`data`), never both. The create/run zod schemas validate the
 * exactly-one constraint at runtime and produce values typed as this union.
 */
export type TextMessage = {
  user: string;
  message: string;
};

export type StructuredMessage = {
  user: string;
  data: unknown;
};

export type ExampleMessage = TextMessage | StructuredMessage;

/** Narrows an ExampleMessage to its structured variant. */
export const isStructuredMessage = ( msg: ExampleMessage ): msg is StructuredMessage => 'data' in msg;

export type Example = {
  id: string;
  type: ExampleTypeEnum;
  data: ExampleMessage[];
  meta_data?: Record<string, unknown> | null;
  tags?: Record<string, string | number | boolean> | null;
};



/* ===== API TYPES ===== */

// ============================================================================
// EVALUATIONS
// ============================================================================

export type EvaluationCreateReq = {
  name: string;
  description?: string | null;
  metric_providers: {
    metric_provider_id: string;
    version?: number | string | null;
  }[];
};

export type EvaluationCreateRes = Evaluation;

export type EvaluationsGetParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  name?: string;
  is_deprecated?: boolean;
  created_at_from?: string;
  created_at_to?: string;
  include_details?: boolean;
};

export type EvaluationsGetRes = {
  items: EvaluationWithDetails<DetailRunInfo>[];
  pagination: {
    next_cursor: string | null;
    has_more: boolean;
    limit: number;
  };
};

export type EvaluationGetParams = {
  include_details?: boolean;
};

export type EvaluationGetRes = EvaluationWithDetails<DetailRunInfo>;

export type EvaluationUpdateReq = {
  is_deprecated: boolean;
};

export type EvaluationUpdateRes = Evaluation;

export type EvaluationsMigrateReq = {
  metric_version: {
    old_id: string;
    old_version: number;
    new_id: string;
    new_version: number;
  }[];
};

export type EvaluationsMigrateRes = {
  succeeded: Evaluation[];
  failed: {
    evaluation_id: string;
    reason: string;
  }[];
};

export type EvaluationRunsListParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  include_details?: boolean;
};

export type EvaluationRunsListRes = {
  items: RunWithDetails<DetailMetricsInfo>[];
  pagination: {
    next_cursor: string | null;
    has_more: boolean;
    limit: number;
  };
};

// ============================================================================
// METRIC PROVIDERS
// ============================================================================

export type MetricProviderCreateReq = {
  name: string;
  description: string;
  example_type: ExampleTypeEnum[];
  configuration: MetricConfigAPI | MetricConfigLLMScore;
  produces_metrics: string[];
};

export type MetricProviderCreateRes = MetricProvider;

export type MetricProvidersGetParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  name?: string;
  created_at_from?: string;
  created_at_to?: string;
  is_deprecated?: boolean;
};

export type MetricProvidersGetRes = {
  items: MetricConfig[];
  pagination: {
    next_cursor: string | null;
    has_more: boolean;
    limit: number;
  };
};

export type MetricProviderGetRes = MetricProvider;

export type MetricProviderVersionGetRes = MetricConfig;

export type MetricProviderVersionCreateReq = {
  example_type: ExampleTypeEnum[];
  configuration: MetricConfigAPI | MetricConfigLLMScore;
  produces_metrics: string[];
};

export type MetricProviderVersionCreateRes = MetricConfig;

export type MetricProviderVersionUpdateReq = {
  is_deprecated: boolean;
};

export type MetricProviderVersionUpdateRes = MetricConfig;

export type MetricProviderVersionCloneReq = {
  name?: string | null;
  name_suffix?: string | null;
  name_prefix?: string | null;
  description?: string | null;
  configuration?: MetricConfigAPI | MetricConfigLLMScore | null;
  example_type?: ExampleTypeEnum[] | null;
  produces_metrics?: string[] | null;
};

export type MetricProviderVersionCloneRes = MetricProvider;

export type MetricProvidersDeprecateReq = {
  model: string; // ModelName
  is_deprecated: boolean;
  scope: 'all' | 'specific'; // MetricProviderScope
  metric_provider_ids?: string[] | null;
  update_evaluations?: boolean;
};

export type MetricProvidersDeprecateRes = {
  succeeded_metrics: MetricConfig[];
  succeeded_evaluations: Evaluation[];
  failed_metric_providers: string[];
  failed_evaluations: string[];
};

export type MetricProvidersMigrateReq = {
  model: {
    old_names?: string[] | null;
    new_name: string;
  };
  migrate_type?: 'clone' | 'new_version';
  metric_provider_ids?: string[] | null;
  evaluation_ids?: string[] | null;
  clone_info?: MetricProviderVersionCloneReq | null;
};

export type MetricProvidersMigrateRes = {
  succeeded: MetricProvider[];
  failed: {
    metric_provider_id: string;
    reason: string;
  }[];
  skipped: {
    metric_provider_id: string;
    reason: string;
  }[];
  version_replacements: {
    old_id: string;
    old_version: number;
    new_id: string;
    new_version: number;
  }[];
};

export type RunCreateReq = {
  data_source: {
    data_origin: 'reference';
    source_id: string;
    source_version: number | 'latest';
  } | {
    data_origin: 'inline';
    examples: Array<{
      id?: string;
      type: 'message' | 'conversation';
      data: Array<{
        user: 'user' | 'assistant';
        message: string;
      }>;
      meta_data?: Record<string, unknown>;
    }>;
  };
  include_reason: boolean;
};

export type RunCreateRes = Run;

export type RunGetRes = RunWithDetails<DetailMetricsInfo>;

export type RunsGetParams = {
  evaluation_id?: string;
  include_details?: boolean;
};

export type RunsGetRes = RunWithDetails<DetailMetricsInfo>[];

export type RunResultsListParams = {
  created_by?: string;
  date_from?: string;
  date_to?: string;
  result_statuses?: string[];
  metric_provider_id?: string;
  metric_name?: string;
  limit?: number;
  offset?: number;
};

export type RunResultsGetRes = Result[];

export type RunResultsListRes = {
  items: Result[];
  has_more: boolean;
};

export type RunResultsCountParams = {
  run_id?: string;
};

export type RunResultsCountRes = {
  count: number;
};

export type RunResultsCountTotalParams = {
  created_by?: string;
  date_from?: string;
  date_to?: string;
  group_by?: string;
  result_statuses?: string[];
};

export type RunResultsCountTotalRes = {
  groups: Record<string, number>;
};

// Comparison query types - matches backend structure exactly
type ComparisonMetricIdentity = {
  metric_provider_id: string;
  metric_provider_version: number;
  metric_name: string;
};

type DateRangeBound = {
  start: string;
  end: string;
};

type IndirectRunsSpec = {
  evaluation_id?: string | null;
  num_last_runs?: number | null;
  date_range?: DateRangeBound | null;
};

type DirectRunsSpec = {
  run_ids: string[];
  date_range?: DateRangeBound | null;
};

type SourceVersionsSpec = {
  sources: Array<{
    source_id: string;
    source_version: number;
  }>;
};

type SingleSourceSpec = {
  source_id: string;
  source_versions?: number[] | null;
};

type ExamplesSpec = {
  example_ids: string[];
};

type ComparisonQueryBase = {
  metric_spec: ComparisonMetricIdentity;
  runs_spec: IndirectRunsSpec | DirectRunsSpec;
  sources_spec?: SourceVersionsSpec | SingleSourceSpec | ExamplesSpec | null;
  created_by?: string | null;
  result_statuses?: string[] | null;
};

// Discriminated union based on comparison_axis
export type CategoricalCompQuery = ComparisonQueryBase & {
  comparison_axis: 'runs' | 'schedules' | 'examples';
};

export type DateRangeCompQuery = ComparisonQueryBase & {
  comparison_axis: 'date_range';
  runs_spec: IndirectRunsSpec | DirectRunsSpec;
  num_buckets: number;
};

export type SingleSourceCompQuery = ComparisonQueryBase & {
  comparison_axis: 'source_versions';
  sources_spec: SingleSourceSpec;
};

export type RunResultsCompareReq =
  | CategoricalCompQuery
  | DateRangeCompQuery
  | SingleSourceCompQuery;

export type RunResultsCompareRes = {
  axis: string;
  metric: ComparisonMetricIdentity;
  buckets: Array<{
    label: string;
    count: number;
    average_score: number | null;
  }>;
};

export type ScheduleState = 'ACTIVE' | 'PAUSED' | 'DISABLED';

export type ScheduleCreateReq = {
  evaluation_id: string;
  source_id: string;
  source_version?: number;
  name?: string;
  description?: string;
  cron_expression: string;
  timezone?: string;
};

export type SourceCreateReq<T = unknown> = {
  name: string;
  description?: string;
  source_type: SourceTypeEnum;
  config: T;
  example_type: ExampleTypeEnum;
};

export type SourceUpdateReq = {
  config: SourceConfig;
};

export type SourceUpdateRes = Source;

export type SourceCreateRes = Source;

export type SourceGetRes = Source;

export type SourcePreviewGetRes = SourcePreview;

export type SourcesSearchParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  name?: string;
  source_type?: string;
  example_type?: string;
  created_at_from?: string;
  created_at_to?: string;
  is_deprecated?: boolean;
};

export type SourcesSearchRes = {
  items: Source[];
  pagination: {
    next_cursor: string | null;
    has_more: boolean;
    limit: number;
  };
};

export type SourceValidateRes = {
  status: 'success' | 'error';
  message: string;
  total_examples?: number;
};

export type SourceVersionCreateReq = {
  config: SourceConfig;
  description?: string;
};

export type SourceVersionCreateRes = Source;

export type SourceVersionValidateReq = {
  config: SourceConfig;
};

export type SourceVersionValidateRes = SourceValidateRes;

export type SourceDiagnosticsReq = {
  source_id: string;
  version: number | 'latest';
};

export type SourceDiagnostic = {
  level: 'info' | 'warning' | 'error';
  message: string;
  details?: Record<string, unknown>;
};

export type SourceDiagnosticsRes = {
  diagnostics: SourceDiagnostic[];
};

export type SchedulesGetParams = {
  // Search/filter fields
  name?: string;
  description?: string;
  created_by?: string;
  created_at_from?: string;
  created_at_to?: string;
  // Schedule-specific filters
  evaluation_id?: string;
  source_id?: string;
  state?: ScheduleState;
  // Pagination & sorting
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  // Attach run statistics (run_count, last_run_id, last_run_status, last_fire_at) to each item
  include_details?: boolean;
};

export type SchedulesGetRes = {
  items: ScheduleWithDetails[];
  pagination: {
    next_cursor: string | null;
    has_more: boolean;
    limit: number;
  };
};

export type ScheduleGetRes = ScheduleWithDetails;

export type SchedulesPauseReq = {
  schedule_ids?: string[];
  evaluation_id?: string;
};

export type SchedulesPauseRes = {
  paused_ids: string[];
  failed_ids: string[];
};

export type SchedulesResumeReq = {
  schedule_ids?: string[];
  evaluation_id?: string;
};

export type SchedulesResumeRes = {
  resumed_ids: string[];
  failed_ids: string[];
};

export type SchedulesDeleteReq = {
  schedule_ids?: string[];
  evaluation_id?: string;
};

export type SchedulesDeleteRes = {
  deleted_ids: string[];
  failed_ids: string[];
};

export type ScheduleDisabledReason =
  | { type: 'evaluation_deprecated'; evaluation_id: string }
  | { type: 'metric_provider_version_deprecated'; metric_provider_version_id: string }
  | { type: 'metric_invalid_llm_model'; metric_provider_version_id: string; llm_model: string; detail: string }
  | { type: 'source_unavailable'; source_id: string; detail: string }
  | { type: 'source_version_unavailable'; source_id: string; source_version: number }
  | { type: 'source_deprecated'; source_id: string; source_version: number };

export type ScheduleResponse = {
  id: string;
  created_at: string;
  updated_at?: string | null;
  created_by?: string | null;
  evaluation_id: string;
  source_id: string;
  source_version: number | null;
  name: string | null;
  description: string | null;
  cron_expression: string;
  timezone: string;
  state: 'ACTIVE' | 'PAUSED' | 'DISABLED';
  next_fire_at: string;
  disabled_reason: ScheduleDisabledReason | null;
};

export type ScheduleWithDetails = {
  schedule: ScheduleResponse;
  details: {
    run_count: number;
    last_run_id: string | null;
    last_run_status: string | null;
    last_fire_at: string | null;
  } | null;
};

export type BulkScheduleSelector = {
  evaluation_id?: string;
  source_id?: string;
  source_version?: number;
};

export type DeleteScheduleResult = {
  fga_cleanup_failed_ids: string[];
};

export type BulkScheduleResult = {
  affected_schedule_ids: string[];
  fga_cleanup_failed_ids: string[];
};

// SSE streaming types for run progress
export const TERMINAL_RUN_STATUSES = [ 'completed', 'failed', 'cancelled', 'incomplete' ] as const;

export type RunProgressMessage = {
  run_id: string;
  run_status: string;
  total_sync_tasks: number;
  total_async_tasks: number;
  sync_running: number;
  sync_succeeded: number;
  sync_failed: number;
  async_submitting: number;
  async_submit_succeeded: number;
  async_submit_failed: number;
  sync_percent_complete: number;
};

export type RunCompletedMessage = {
  run_id: string;
  run_status: string;
};

// Shared type for PATCH update operations that only update deprecation status
// Used by: EvaluationUpdateReq, SourceVersionUpdateReq
export type DeprecateReq = {
  is_deprecated: boolean;
};

export type SourceVersionUpdateReq = DeprecateReq;


/* ===== UI TYPES ===== */
export type ExampleMetricDataMap = {
  [ exampleId: string ]: {
    [ MetricProviderIdPipeMetricName: string ]: Result;
  }
};

export type RunMetricDataMap = {
  [ runId: string ]: MetricDataMap;
};

export type MetricDataMap = {
  [ MetricProviderIdPipeMetricName: string ]: DetailMetricInfo;
};
